/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Divider,
  Stack,
  Box,
  Text,
  SimpleGrid,
  Flex,
  Badge,
} from '@chakra-ui/react';
import React from 'react';
import { CustomizedFields, Order } from '@/services/OrderManagement/types';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { AddOnData } from '@/services/addProduct/types';

interface ICustomizationDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  order?: Order;
}

const formatFieldName = (key: string): string => {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const CustomizationDetailsModal: React.FC<ICustomizationDetailsModalProps> = ({
  isOpen,
  onClose,
  order,
}) => {
  const customizedFields = (order?.customized_fields ||
    order?.product_info?.[0]?.cart_item?.customized_fields) as CustomizedFields | undefined;

  if (!customizedFields) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay bg="rgba(0, 0, 0, 0.6)" backdropFilter="blur(2px)" />
        <ModalContent bg="background.secondary" rounded="xl">
          <ModalHeader>Customization Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody py={8}>
            <Text textAlign="center" color="text.secondary">
              No customization details available.
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  const addOns: AddOnData[] = (customizedFields.add_ons as AddOnData[]) || ([] as AddOnData[]);
  const currency =
    addOns[0]?.currency || (customizedFields as any).currency || order?.currency || 'NPR';

  const unit = (customizedFields.unit as string) || 'inches';
  const measurements = customizedFields.measurements as
    | { upper?: Record<string, any>; lower?: Record<string, any> }
    | Record<string, any>
    | undefined;

  // Detect nested upper/lower structure
  const hasUpperLower =
    measurements &&
    typeof measurements === 'object' &&
    ('upper' in measurements || 'lower' in measurements);

  const upperMeasurements = hasUpperLower
    ? Object.entries(measurements.upper || {}).filter(([, v]) => v != null)
    : [];

  const lowerMeasurements = hasUpperLower
    ? Object.entries(measurements.lower || {}).filter(([, v]) => v != null)
    : [];

  const flatMeasurementEntries = Object.entries(customizedFields).filter(([key, value]) => {
    const excluded = [
      'unit',
      'add_ons',
      'currency',
      'category_id',
      'blouse_style',
      'kurta_style',
      'size_type',
      'size_selection',
      'is_padding_required',
      'customized_price',
      'ProductAddOn_ids',
      'measurements',
    ];
    return (
      !excluded.includes(key) &&
      (typeof value === 'string' || typeof value === 'number') &&
      String(value).trim() !== '' &&
      !key.includes('style')
    );
  });

  const renderStyles = () => {
    const styles =
      (customizedFields.blouse_style as string[] | undefined) ||
      (customizedFields.kurta_style as string[] | undefined) ||
      [];

    if (!Array.isArray(styles) || styles.length === 0) return null;

    const label = customizedFields.category_id === 2 ? 'Blouse Style' : 'Kurta Style';

    return (
      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>
          {label}:
        </Text>
        <Flex gap={2} flexWrap="wrap">
          {styles.map((style: any, idx: number) => (
            <Badge key={idx} variant="subtle">
              {typeof style === 'string' ? style : style.name || 'Style'}
            </Badge>
          ))}
        </Flex>
      </Box>
    );
  };
  // Use top-level order.customized_price instead of customized_fields.customized_price
  const customizationPrice = order?.customized_price;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="rgba(0, 0, 0, 0.6)" backdropFilter="blur(2px)" />
      <ModalContent bg="background.secondary" rounded="xl" maxW="4xl">
        <ModalHeader
          fontSize="xl"
          fontWeight="semibold"
          fontFamily="primary"
          color="text.primary"
          bg="white"
          borderRadius="12px 12px 0 0"
        >
          Customization Details
        </ModalHeader>
        <ModalCloseButton />
        <Divider borderColor="border.default" />
        <ModalBody py={6} fontFamily="primary">
          <Stack spacing={6}>
            {/* CASE 1: Proper Upper / Lower exists → Show grouped */}
            {hasUpperLower ? (
              <Box
                border="1px solid"
                borderColor="border.default"
                borderRadius="lg"
                p={5}
                bg="white"
              >
                <Text fontSize="lg" fontWeight="semibold" mb={5}>
                  Measurements ({unit})
                </Text>
                <Stack spacing={6}>
                  {upperMeasurements.length > 0 && (
                    <Box>
                      <Text fontSize="md" fontWeight={'bold'} mb={3}>
                        Upper Body
                      </Text>
                      <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
                        {upperMeasurements.map(([key, value]) => (
                          <Text key={key} fontSize="sm">
                            <strong>{formatFieldName(key)}:</strong> {String(value)} {unit}
                          </Text>
                        ))}
                      </SimpleGrid>
                    </Box>
                  )}
                  {lowerMeasurements.length > 0 && (
                    <Box>
                      <Text fontSize="md" fontWeight={'bold'} mb={3}>
                        Lower Body
                      </Text>
                      <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
                        {lowerMeasurements.map(([key, value]) => (
                          <Text key={key} fontSize="sm">
                            <strong>{formatFieldName(key)}:</strong> {String(value)} {unit}
                          </Text>
                        ))}
                      </SimpleGrid>
                    </Box>
                  )}
                </Stack>
              </Box>
            ) : (
              /* CASE 2: Only flat measurements */
              (flatMeasurementEntries.length > 0 ||
                (measurements && !hasUpperLower && Object.keys(measurements).length > 0)) && (
                <Box
                  border="1px solid"
                  borderColor="border.default"
                  borderRadius="lg"
                  p={5}
                  bg="white"
                >
                  <Text fontSize="lg" fontWeight="semibold" mb={4}>
                    Measurements ({unit})
                  </Text>
                  <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
                    {flatMeasurementEntries.map(([key, value]) => (
                      <Text key={`root-${key}`} fontSize="sm">
                        <strong>{formatFieldName(key)}:</strong> {value} {unit}
                      </Text>
                    ))}
                    {measurements &&
                      !hasUpperLower &&
                      Object.entries(measurements)
                        .filter(([, v]) => v != null && typeof v === 'string')
                        .map(([key, value]) => (
                          <Text key={`meas-${key}`} fontSize="sm">
                            <strong>{formatFieldName(key)}:</strong> {value} {unit}
                          </Text>
                        ))}
                  </SimpleGrid>
                </Box>
              )
            )}

            {/* Preferences */}
            {(customizedFields.size_selection ||
              customizedFields.is_padding_required !== undefined) && (
              <Box
                border="1px solid"
                borderColor="border.default"
                borderRadius="lg"
                p={5}
                bg="white"
              >
                <Text fontSize="lg" fontWeight="semibold" mb={4}>
                  Preferences
                </Text>
                <SimpleGrid columns={2} spacing={4}>
                  {customizedFields.size_selection && (
                    <Text fontSize="sm">
                      <strong>Size Selection:</strong> {customizedFields.size_selection}
                    </Text>
                  )}
                  {customizedFields.is_padding_required !== undefined && (
                    <Text fontSize="sm">
                      <strong>Padding Required:</strong>{' '}
                      {customizedFields.is_padding_required ? 'Yes' : 'No'}
                    </Text>
                  )}
                </SimpleGrid>
              </Box>
            )}

            {/* Styles */}
            {renderStyles()}

            {/* Customization Price – Now from order.customized_price */}
            {customizationPrice != null && Number(customizationPrice) > 0 && (
              <Flex
                justify="space-between"
                p={4}
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="border.default"
              >
                <Text fontWeight="semibold">Customization Price</Text>
                <Text fontWeight="bold">
                  {getCurrencySymbol(currency)}
                  {Number(customizationPrice).toFixed(2)}
                </Text>
              </Flex>
            )}

            {/* Add-Ons */}
            {addOns.length > 0 && (
              <Box
                border="1px solid"
                borderColor="border.default"
                borderRadius="lg"
                p={5}
                bg="white"
              >
                <Text fontSize="lg" fontWeight="semibold" mb={4}>
                  Add-Ons
                </Text>
                <Stack spacing={3}>
                  {addOns.map((addOn: AddOnData, index: number) => (
                    <Flex key={index} justify="space-between">
                      <Text fontSize="sm">{addOn.name || addOn.title || 'Add-On'}</Text>
                      <Text fontSize="sm" fontWeight="semibold">
                        {getCurrencySymbol(addOn.currency || currency)}
                        {addOn.price}
                      </Text>
                    </Flex>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CustomizationDetailsModal;
